import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    Alert,
    Button,
    Card,
    Col,
    Form,
    Input,
    Modal,
    Popconfirm,
    Row,
    Space,
    Switch,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import {
    BellOutlined,
    DeleteOutlined,
    EditOutlined,
    PlayCircleOutlined,
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined,
    SyncOutlined
} from '@ant-design/icons';
import Layout from './Layout';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const renderMatchDetails = matches => {
    const list = matches || [];
    if (!list.length) return null;
    if (typeof list[0] === 'string') {
        return <Tag color="green">найдено: {list.join(', ')}</Tag>;
    }
    return (
        <Space direction="vertical" size={4} style={{ maxWidth: '100%' }}>
            {list.map((m, index) => (
                <div key={`${m.page_url || ''}-${m.keyword}-${index}`}>
                    <Text type="success">
                        «{m.keyword}»: {m.snippet}
                    </Text>
                    {m.page_url ? (
                        <div>
                            <a
                                href={m.page_url}
                                target="_blank"
                                rel="noreferrer"
                                style={{ fontSize: 12 }}
                            >
                                {m.page_url}
                            </a>
                        </div>
                    ) : null}
                </div>
            ))}
        </Space>
    );
};

const KeywordWatchPage = () => {
    const [sites, setSites] = useState([]);
    const [logs, setLogs] = useState([]);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();
    const [configForm] = Form.useForm();

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [sitesRes, logsRes, configRes] = await Promise.all([
                axios.get('/api/keyword-watch/sites', {
                    headers: authHeaders()
                }),
                axios.get('/api/keyword-watch/logs', {
                    params: { limit: 50, only_new: 1 },
                    headers: authHeaders()
                }),
                axios.get('/api/keyword-watch/config', {
                    headers: authHeaders()
                })
            ]);
            setSites(sitesRes.data.data || []);
            setLogs(logsRes.data.data || []);
            const cfg = configRes.data.data || {};
            setConfig(cfg);
            configForm.setFieldsValue({
                notify_phone: cfg.notify_phone || '89397531841',
                notify_user_id: cfg.notify_user_id || '',
                notify_chat_id: cfg.notify_chat_id || ''
            });
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Ошибка загрузки мониторинга'
            );
        } finally {
            setLoading(false);
        }
    }, [configForm]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const openModal = (row = null) => {
        setEditing(row);
        setModalOpen(true);
        if (row) {
            form.setFieldsValue({
                name: row.name,
                url: row.url,
                keywords: (row.keywords || []).join('\n'),
                is_active: row.is_active !== false
            });
        } else {
            form.resetFields();
            form.setFieldsValue({ is_active: true });
        }
    };

    const saveSite = async values => {
        try {
            const payload = {
                ...values,
                keywords: values.keywords
            };
            if (editing) {
                await axios.put(
                    `/api/keyword-watch/sites/${editing.id}`,
                    payload,
                    { headers: authHeaders() }
                );
                message.success('Сайт обновлён');
            } else {
                await axios.post('/api/keyword-watch/sites', payload, {
                    headers: authHeaders()
                });
                message.success('Сайт добавлен');
            }
            setModalOpen(false);
            setEditing(null);
            form.resetFields();
            loadAll();
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось сохранить'
            );
        }
    };

    const removeSite = async id => {
        try {
            await axios.delete(`/api/keyword-watch/sites/${id}`, {
                headers: authHeaders()
            });
            message.success('Удалено');
            loadAll();
        } catch (error) {
            message.error(
                error.response?.data?.message || 'Ошибка удаления'
            );
        }
    };

    const saveConfig = async values => {
        try {
            const { data } = await axios.put(
                '/api/keyword-watch/config',
                values,
                { headers: authHeaders() }
            );
            setConfig(data.data);
            message.success('Настройки сохранены');
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Ошибка сохранения настроек'
            );
        }
    };

    const syncRecipients = async () => {
        try {
            const { data } = await axios.post(
                '/api/keyword-watch/sync-recipients',
                {},
                { headers: authHeaders() }
            );
            setConfig(prev => ({ ...prev, ...data.data }));
            configForm.setFieldsValue({
                notify_user_id: data.data.notify_user_id || '',
                notify_chat_id: data.data.notify_chat_id || ''
            });
            message.success('Получатель обновлён из диалогов бота');
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось определить получателя'
            );
        }
    };

    const runCheck = async () => {
        setRunning(true);
        try {
            const { data } = await axios.post(
                '/api/keyword-watch/run',
                {},
                { headers: authHeaders() }
            );
            const payload = data.data || {};
            if (payload.started === false) {
                message.warning('Проверка уже выполняется');
                return;
            }

            message.info(
                'Проверка запущена в фоне. Обновление каждые 3 сек…'
            );

            const startedAt = Date.now();
            while (Date.now() - startedAt < 240000) {
                await new Promise(resolve => setTimeout(resolve, 3000));
                const statusRes = await axios.get(
                    '/api/keyword-watch/run-status',
                    { headers: authHeaders() }
                );
                await loadAll();
                if (!statusRes.data?.data?.running) {
                    message.success('Проверка завершена');
                    return;
                }
            }

            await loadAll();
            message.success('Проверка завершена (или ещё выполняется)');
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Ошибка проверки'
            );
        } finally {
            setRunning(false);
        }
    };

    const testNotify = async () => {
        try {
            await axios.post(
                '/api/keyword-watch/test-notify',
                {},
                { headers: authHeaders() }
            );
            message.success('Тестовое сообщение отправлено в MAX');
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось отправить тест'
            );
        }
    };

    const siteColumns = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'URL',
            dataIndex: 'url',
            key: 'url',
            render: url => (
                <a href={url} target="_blank" rel="noreferrer">
                    {url}
                </a>
            )
        },
        {
            title: 'Ключевые слова',
            key: 'keywords',
            render: (_, row) =>
                (row.keywords || []).map(kw => (
                    <Tag key={kw}>{kw}</Tag>
                ))
        },
        {
            title: 'Последняя проверка',
            key: 'last',
            width: 280,
            render: (_, row) =>
                row.last_checked_at ? (
                    <Space direction="vertical" size={0}>
                        <Text>
                            {new Date(
                                row.last_checked_at
                            ).toLocaleString('ru-RU')}
                        </Text>
                        {row.pages_checked ? (
                            <Text type="secondary">
                                страниц: {row.pages_checked}
                            </Text>
                        ) : null}
                        {row.last_error ? (
                            row.last_error === 'Проверка выполняется…' ? (
                                <Tag color="processing">
                                    проверка…
                                </Tag>
                            ) : (
                                <Text type="danger">{row.last_error}</Text>
                            )
                        ) : (row.last_new_matches || []).length ? (
                            <Tag color="green">
                                новых: {(row.last_new_matches || []).length}
                            </Tag>
                        ) : row.last_found ? (
                            <Tag color="default">новых нет</Tag>
                        ) : (
                            <Tag>не найдено</Tag>
                        )}
                    </Space>
                ) : (
                    '—'
                )
        },
        {
            title: 'Активен',
            dataIndex: 'is_active',
            key: 'is_active',
            width: 90,
            render: v =>
                v !== false ? (
                    <Tag color="green">да</Tag>
                ) : (
                    <Tag>нет</Tag>
                )
        },
        {
            title: '',
            key: 'actions',
            width: 100,
            render: (_, row) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => openModal(row)}
                    />
                    <Popconfirm
                        title="Удалить сайт?"
                        onConfirm={() => removeSite(row.id)}
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const logColumns = [
        {
            title: 'Время',
            dataIndex: 'checked_at',
            key: 'checked_at',
            width: 160,
            render: v =>
                v
                    ? new Date(v).toLocaleString('ru-RU')
                    : '—'
        },
        {
            title: 'Сайт',
            dataIndex: 'site_name',
            key: 'site_name'
        },
        {
            title: 'Результат',
            key: 'result',
            render: (_, row) =>
                row.error_text ? (
                    <Text type="danger">{row.error_text}</Text>
                ) : row.found ? (
                    renderMatchDetails(row.matches)
                ) : (
                    <Tag>не найдено</Tag>
                )
        },
        {
            title: 'MAX',
            key: 'notify',
            width: 100,
            render: (_, row) =>
                row.notification_sent ? (
                    <Tag color="blue">отправлено</Tag>
                ) : (
                    <Tag>{row.notification_error || '—'}</Tag>
                )
        }
    ];

    return (
        <Layout>
            <div style={{ padding: 24 }}>
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        marginBottom: 16
                    }}
                    wrap
                >
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            <SearchOutlined /> Мониторинг ключевых
                            слов
                        </Title>
                        <Text type="secondary">
                            Обход страниц сайта, только новые совпадения,
                            оповещение в MAX (бот Smartkom)
                        </Text>
                    </div>
                    <Space wrap>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadAll}
                        >
                            Обновить
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            loading={running}
                            onClick={runCheck}
                        >
                            Проверить сейчас
                        </Button>
                    </Space>
                </Space>

                {!config?.bot_configured ? (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="Токен бота не задан"
                        description="Добавьте SMARTKOM_MAX_BOT_TOKEN в backend/.env и перезапустите сервер."
                    />
                ) : null}

                <Row gutter={16}>
                    <Col xs={24} lg={8}>
                        <Card
                            title="Получатель MAX"
                            style={{ marginBottom: 16 }}
                        >
                            <Form
                                form={configForm}
                                layout="vertical"
                                onFinish={saveConfig}
                            >
                                <Form.Item
                                    name="notify_phone"
                                    label="Телефон"
                                >
                                    <Input placeholder="89397531841" />
                                </Form.Item>
                                <Form.Item
                                    name="notify_user_id"
                                    label="MAX user_id"
                                    extra="Определяется автоматически, если пользователь писал боту"
                                >
                                    <Input placeholder="123456789" />
                                </Form.Item>
                                <Form.Item
                                    name="notify_chat_id"
                                    label="MAX chat_id"
                                >
                                    <Input placeholder="опционально" />
                                </Form.Item>
                                <Space wrap>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                    >
                                        Сохранить
                                    </Button>
                                    <Button
                                        icon={<SyncOutlined />}
                                        onClick={syncRecipients}
                                    >
                                        Из диалогов бота
                                    </Button>
                                    <Button
                                        icon={<BellOutlined />}
                                        onClick={testNotify}
                                    >
                                        Тест MAX
                                    </Button>
                                </Space>
                            </Form>
                            <Paragraph
                                type="secondary"
                                style={{ marginTop: 12 }}
                            >
                                Чтобы бот мог писать на номер{' '}
                                {config?.notify_phone ||
                                    '89397531841'}
                                , пользователь должен один раз
                                написать боту Smartkom в MAX,
                                затем нажать «Из диалогов бота».
                            </Paragraph>
                        </Card>
                    </Col>
                    <Col xs={24} lg={16}>
                        <Card
                            title="Сайты для проверки"
                            extra={
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => openModal()}
                                >
                                    Добавить
                                </Button>
                            }
                            style={{ marginBottom: 16 }}
                        >
                            <Table
                                rowKey="id"
                                loading={loading}
                                columns={siteColumns}
                                dataSource={sites.filter(
                                    site => site.is_active !== false
                                )}
                                pagination={{ pageSize: 10 }}
                                size="middle"
                            />
                        </Card>
                    </Col>
                </Row>

                <Card title="Журнал новых совпадений">
                    <Table
                        rowKey="id"
                        loading={loading}
                        columns={logColumns}
                        dataSource={logs}
                        pagination={{ pageSize: 15 }}
                        size="small"
                        locale={{
                            emptyText:
                                'Новых совпадений пока нет — повторы не показываются'
                        }}
                    />
                </Card>
            </div>

            <Modal
                title={
                    editing
                        ? 'Редактировать сайт'
                        : 'Новый сайт'
                }
                open={modalOpen}
                onCancel={() => {
                    setModalOpen(false);
                    setEditing(null);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={saveSite}
                >
                    <Form.Item
                        name="name"
                        label="Название"
                        rules={[{ required: true }]}
                    >
                        <Input placeholder="Сайт конкурента" />
                    </Form.Item>
                    <Form.Item
                        name="url"
                        label="URL"
                        rules={[{ required: true }]}
                    >
                        <Input placeholder="https://example.com/news" />
                    </Form.Item>
                    <Form.Item
                        name="keywords"
                        label="Ключевые слова"
                        extra="По одному в строке или через запятую"
                        rules={[{ required: true }]}
                    >
                        <TextArea
                            rows={4}
                            placeholder={'Smartcom\nСМАРТКОМ\nбазовая станция'}
                        />
                    </Form.Item>
                    <Form.Item
                        name="is_active"
                        label="Активен"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default KeywordWatchPage;
